import { useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Loader2, Upload, MessageSquare, Sparkles, AlertTriangle } from 'lucide-react'
import { useContextEngineStore } from '@/stores/contextEngineStore'
import { ChunkList } from '@/components/context/ChunkList'
import { DOMAIN_LABELS, ALL_DOMAINS } from '@/types/contextEngine'
import { DOMAIN_ICONS } from '@/components/context/domainIcons'
import type { ContextDomain, DetectedGap, DomainScore } from '@/types/contextEngine'

// ─── Gap card ───────────────────────────────────────────────────────────────

const RESOLUTION_CONFIG: Record<
  DetectedGap['suggestedResolution']['method'],
  { icon: typeof Upload; label: string; mode: string }
> = {
  request_document: { icon: Upload, label: 'Upload Document', mode: 'upload' },
  ask_question: { icon: MessageSquare, label: 'Start Interview', mode: 'interview' },
  infer_from_existing: { icon: Sparkles, label: 'Infer from Context', mode: '' },
}

function GapCard({ gap }: { gap: DetectedGap }) {
  const config = RESOLUTION_CONFIG[gap.suggestedResolution.method]
  const Icon = config.icon

  const href = config.mode
    ? `/context/add?mode=${config.mode}&domain=${gap.domain}&gap=${gap.gapId}`
    : '#'

  const severityColor =
    gap.severity >= 0.7
      ? 'border-accent-error/30 bg-accent-error/5'
      : gap.severity >= 0.4
        ? 'border-accent-amber/30 bg-accent-amber/5'
        : 'border-border'

  return (
    <div className={`rounded-lg border p-4 ${severityColor}`}>
      <div className="mb-1 flex items-start gap-2">
        <AlertTriangle
          size={14}
          className={
            gap.severity >= 0.7
              ? 'mt-0.5 text-accent-error'
              : 'mt-0.5 text-accent-amber'
          }
        />
        <p className="flex-1 text-sm text-text-primary">{gap.description}</p>
      </div>

      {/* Suggested questions */}
      {gap.suggestedResolution.questions && gap.suggestedResolution.questions.length > 0 && (
        <ul className="mb-3 ml-5 mt-2 space-y-1">
          {gap.suggestedResolution.questions.slice(0, 3).map((q, i) => (
            <li key={i} className="text-xs text-text-secondary">
              &bull; {q}
            </li>
          ))}
        </ul>
      )}

      <Link
        to={href}
        className="inline-flex items-center gap-1.5 rounded-md bg-bg-tertiary px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-accent-amber/10 hover:text-accent-amber"
      >
        <Icon size={14} />
        {config.label}
      </Link>
    </div>
  )
}

// ─── Domain header ──────────────────────────────────────────────────────────

function DomainHeader({ domain, score }: { domain: ContextDomain; score: DomainScore | null }) {
  const navigate = useNavigate()
  const Icon = DOMAIN_ICONS[domain]
  const label = DOMAIN_LABELS[domain]

  return (
    <div className="mb-6">
      <button
        onClick={() => navigate('/context')}
        className="mb-4 inline-flex items-center gap-1 text-xs text-text-tertiary transition-colors hover:text-accent-amber"
      >
        <ArrowLeft size={14} />
        Back to Dashboard
      </button>

      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-amber/10">
          <Icon size={24} className="text-accent-amber" />
        </div>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-text-primary">{label}</h1>
          {score && (
            <div className="mt-1 flex items-center gap-4 text-sm text-text-secondary">
              <span>
                Completeness:{' '}
                <span className="font-mono text-accent-amber">{Math.round(score.score)}%</span>
              </span>
              <span>
                Confidence:{' '}
                <span className="font-mono">
                  {Math.round(score.avgConfidence * 100)}%
                </span>
              </span>
              <span>
                {score.activeChunks} chunk{score.activeChunks !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────

/**
 * Legacy Engine v2 (chunks-based) Domain Detail. Still mounted at the
 * legacy `/context/domains/:domain` route until commit 16 relocates the
 * Context Explorer pages under `pages/legacy/`. The new Phase 4 surface
 * lives in `DomainDetailPage.tsx` at `/domains/:domainKey`.
 */
export function LegacyDomainDetailPage() {
  const { domain: domainParam } = useParams<{ domain: string }>()
  const navigate = useNavigate()

  const store = useContextEngineStore()
  const {
    gapAnalysis,
    gapAnalysisLoading,
    fetchGapAnalysis,
    chunks,
    chunksLoading: chunksLoadingMap,
    chunksError,
    fetchChunks,
    updateChunk,
    archiveChunk,
  } = store

  // Validate domain param
  const domain = ALL_DOMAINS.includes(domainParam as ContextDomain)
    ? (domainParam as ContextDomain)
    : null

  // Fetch gap analysis if not loaded
  useEffect(() => {
    if (!gapAnalysis && !gapAnalysisLoading) {
      fetchGapAnalysis()
    }
  }, [gapAnalysis, gapAnalysisLoading, fetchGapAnalysis])

  // Fetch chunks for this domain
  useEffect(() => {
    if (domain) {
      fetchChunks(domain)
    }
  }, [domain, fetchChunks])

  // Handlers for ChunkList
  const handleUpdate = useCallback(
    async (chunkId: string, updates: { text?: string }) => {
      await updateChunk(chunkId, updates)
    },
    [updateChunk],
  )

  const handleArchive = useCallback(
    async (chunkId: string) => {
      if (domain) {
        await archiveChunk(chunkId, domain)
      }
    },
    [archiveChunk, domain],
  )

  // Invalid domain
  if (!domain) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-text-secondary">This domain does not exist.</p>
        <button
          onClick={() => navigate('/context')}
          className="mt-3 text-sm text-accent-amber hover:underline"
        >
          Back to Dashboard
        </button>
      </div>
    )
  }

  // Derive domain score from gap analysis
  const domainScore = gapAnalysis?.domainScores.find((s) => s.domain === domain) ?? null

  // Gaps for this domain
  const domainGaps = (gapAnalysis?.gaps ?? []).filter(
    (g) => g.domain === domain && g.status === 'open',
  )

  const domainChunks = chunks[domain] ?? []

  return (
    <div className="mx-auto max-w-[900px] p-6 lg:p-8">
      <DomainHeader domain={domain} score={domainScore} />

      {/* Gaps section */}
      {domainGaps.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 font-display text-lg font-semibold text-text-primary">
            Gaps ({domainGaps.length})
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {domainGaps.map((gap) => (
              <GapCard key={gap.gapId} gap={gap} />
            ))}
          </div>
        </section>
      )}

      {/* Chunks section */}
      <section>
        <h2 className="mb-3 font-display text-lg font-semibold text-text-primary">
          Knowledge Chunks
        </h2>

        {chunksLoadingMap[domain] && domainChunks.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-accent-amber" size={24} />
          </div>
        ) : chunksError ? (
          <div className="rounded-lg border border-accent-error/30 bg-accent-error/5 p-4 text-center">
            <p className="text-sm text-accent-error">{chunksError}</p>
            <button
              onClick={() => fetchChunks(domain)}
              className="mt-2 text-xs text-accent-amber hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <ChunkList
            chunks={domainChunks}
            onUpdate={handleUpdate}
            onArchive={handleArchive}
          />
        )}
      </section>
    </div>
  )
}
